/**
 * Dynamic Google Fonts Loader
 * 
 * Loads Google Fonts dynamically when user changes font preference.
 * This ensures fonts are actually loaded and applied when selected.
 */

interface FontConfig {
  label: string;
  value: string;
  googleFont?: string; // Google Fonts family name (e.g., 'Roboto', 'Lato')
  weights?: string; // Font weights to load (e.g., '400;500;600;700')
}

export const availableFonts: FontConfig[] = [
  { 
    label: 'Inter', 
    value: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    googleFont: 'Inter',
    weights: '400;500;600;700'
  },
  { 
    label: 'Roboto', 
    value: 'Roboto, sans-serif',
    googleFont: 'Roboto',
    weights: '400;500;700'
  },
  { 
    label: 'Lato', 
    value: 'Lato, sans-serif',
    googleFont: 'Lato',
    weights: '400;700'
  },
  { 
    label: 'Open Sans', 
    value: 'Open Sans, sans-serif',
    googleFont: 'Open Sans',
    weights: '400;600;700'
  },
  { 
    label: 'Poppins', 
    value: 'Poppins, sans-serif',
    googleFont: 'Poppins',
    weights: '400;500;600;700'
  },
  { 
    label: 'Montserrat', 
    value: 'Montserrat, sans-serif',
    googleFont: 'Montserrat',
    weights: '400;500;600;700'
  },
  { 
    label: 'System Default', 
    value: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif'
  },
];

/**
 * Loads a Google Font dynamically by injecting a <link> tag
 */
export function loadGoogleFont(fontFamily: string, weights: string = '400;500;600;700'): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if font is already loaded
    const existingLink = document.querySelector(`link[data-google-font="${fontFamily}"]`);
    if (existingLink) {
      resolve();
      return;
    }

    // Create link element
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@${weights}&display=swap`;
    link.setAttribute('data-google-font', fontFamily);
    
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to load font: ${fontFamily}`));
    
    document.head.appendChild(link);
  });
}

/**
 * Loads a font based on the font family string
 * Extracts Google Font name if present, otherwise assumes system font
 */
export function loadFont(fontFamily: string): Promise<void> {
  // Find matching font config
  const fontConfig = availableFonts.find(f => f.value === fontFamily);
  
  if (!fontConfig || !fontConfig.googleFont) {
    // System font or already loaded, no action needed
    return Promise.resolve();
  }
  
  return loadGoogleFont(fontConfig.googleFont, fontConfig.weights);
}

/**
 * Preloads all available Google Fonts for better performance
 * Call this on app initialization if desired
 */
export function preloadFonts(): void {
  availableFonts.forEach(font => {
    if (font.googleFont) {
      // Preconnect to Google Fonts
      const preconnect = document.createElement('link');
      preconnect.rel = 'preconnect';
      preconnect.href = 'https://fonts.googleapis.com';
      if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
        document.head.appendChild(preconnect);
      }
      
      const preconnect2 = document.createElement('link');
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      if (!document.querySelector('link[href="https://fonts.gstatic.com"]')) {
        document.head.appendChild(preconnect2);
      }
    }
  });
}

